import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams,Link } from "react-router-dom";
import toast from "react-hot-toast";
import AuthModal from "../components/AuthModal.js";
import { ReactComponent as ModalLogo } from "./../assets/svgs/modal-logo.svg";
import { ReactComponent as CheckoutIcon } from "./../assets/svgs/checkout-icon.svg";
import Footer from "../components/Footer.js";

const PRICING = {
  annual: {
    label: "Plus Individual Annual Subscription",
    billingLabel: "Annual",
    monthlyPrice: 3.99,
    billingMonths: 12,
    discountText: "Save 20%",
  },
  monthly: {
    label: "Plus Individual Monthly Subscription",
    billingLabel: "Monthly",
    monthlyPrice: 4.99,
    billingMonths: 1,
    discountText: "",
  },
};

const formatUSD = (amount) => `$${amount.toFixed(2)}`;

const initialFormValues = {
  name: "",
  email: "",
  zipCode: "",
};

const validateName = (value) => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return "Name is required.";
  }

  if (/\d/.test(trimmedValue)) {
    return "Name should not contain numbers.";
  }

  if (!/^[A-Za-z\s.'-]+$/.test(trimmedValue)) {
    return "Name can only contain letters, spaces, apostrophes, periods, and hyphens.";
  }

  return "";
};

const validateEmail = (value) => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return "Email is required.";
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmedValue)) {
    return "Enter a valid email address, for example name@example.com.";
  }

  return "";
};

const validateZipCode = (value) => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return "ZIP code is required.";
  }

  if (!/^\d{5}(-\d{4})?$/.test(trimmedValue)) {
    return "Enter a valid ZIP code, for example 12345 or 12345-6789.";
  }

  return "";
};

const validateForm = (values) => {
  return {
    name: validateName(values.name),
    email: validateEmail(values.email),
    zipCode: validateZipCode(values.zipCode),
  };
};

const hasValidationErrors = (errors) => {
  return Object.values(errors).some(Boolean);
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialBilling = searchParams.get("billing") || "annual";

  const [billingCycle, setBillingCycle] = useState(
    initialBilling === "monthly" ? "monthly" : "annual",
  );
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formValues, setFormValues] = useState(initialFormValues);
  const [formErrors, setFormErrors] = useState(initialFormValues);
  const [touchedFields, setTouchedFields] = useState({});

  const [authTick, setAuthTick] = useState(0);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState("login");

  const token = useMemo(() => {
    return localStorage.getItem("authToken");
  }, [authTick]);

  const isSignedIn = Boolean(token);

  const openLoginModal = () => {
    setAuthModalMode("login");
    setAuthModalOpen(true);
  };

  useEffect(() => {
    const handleAuthChanged = () => {
      setAuthTick((prev) => prev + 1);
    };

    window.addEventListener("eveness-auth-changed", handleAuthChanged);

    return () => {
      window.removeEventListener("eveness-auth-changed", handleAuthChanged);
    };
  }, []);
  const pricing = useMemo(() => {
    const selectedPlan = PRICING[billingCycle];
    const subtotal = selectedPlan.monthlyPrice * selectedPlan.billingMonths;
    const tax = 0;
    const total = subtotal + tax;

    return {
      ...selectedPlan,
      subtotal,
      tax,
      total,
      priceLabel:
        billingCycle === "annual"
          ? `${formatUSD(selectedPlan.monthlyPrice)}/month, billed annually`
          : `${formatUSD(selectedPlan.monthlyPrice)}/month`,
      subtotalLabel:
        billingCycle === "annual"
          ? `${formatUSD(subtotal)}/year`
          : `${formatUSD(subtotal)}/month`,
      taxLabel:
        billingCycle === "annual"
          ? `${formatUSD(tax)}/year`
          : `${formatUSD(tax)}/month`,
      totalLabel:
        billingCycle === "annual"
          ? `USD ${total.toFixed(2)}/year`
          : `USD ${total.toFixed(2)}/month`,
    };
  }, [billingCycle]);

  const getInputClassName = (fieldName) => {
    const hasError = touchedFields[fieldName] && formErrors[fieldName];

    return [
      "h-[40px] w-full rounded-[3px] border-[1px] px-[10px] text-[#111111] outline-none transition sm:h-[45px] sm:rounded-[4px] sm:px-[14px] text-[13px]",
      hasError
        ? "border-[#DC2626] focus:border-[#DC2626]"
        : "border-[#00000033] focus:border-[#FF9A86]",
    ].join(" ");
  };

  const handleFieldChange = (fieldName, value) => {
    const updatedValues = {
      ...formValues,
      [fieldName]: value,
    };

    setFormValues(updatedValues);

    if (touchedFields[fieldName]) {
      setFormErrors(validateForm(updatedValues));
    }
  };

  const handleFieldBlur = (fieldName) => {
    const updatedTouchedFields = {
      ...touchedFields,
      [fieldName]: true,
    };

    setTouchedFields(updatedTouchedFields);
    setFormErrors(validateForm(formValues));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = validateForm(formValues);

    setFormErrors(validationErrors);
    setTouchedFields({
      name: true,
      email: true,
      zipCode: true,
    });

    if (hasValidationErrors(validationErrors)) {
      toast.error("Please fix the highlighted fields before continuing.");
      return;
    }

    try {
      setIsSubmitting(true);

      const checkoutPayload = {
        name: formValues.name.trim(),
        email: formValues.email.trim(),
        zipCode: formValues.zipCode.trim(),
        billingCycle,
        paymentMethod,
        amount: pricing.total,
      };

      console.log("Checkout payload:", checkoutPayload);

      // implement STRIPE FLOW
    } catch (error) {
      toast.error("Failed to start checkout. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <div className="min-h-screen bg-[#F7F7F7] px-[8px] py-[7px] font-inter text-[#111111] sm:px-4 sm:py-5">
      <AuthModal
        isOpen={authModalOpen}
        initialMode={authModalMode}
        onClose={() => setAuthModalOpen(false)}
      />
      <div className="mx-auto w-full max-w-[1420px]">
        <header className="flex h-[48px] items-center justify-between rounded-[4px] border border-[#E3E3E3] bg-white px-[9px] sm:h-[64px] sm:rounded-[5px] sm:px-[14px]">
          <button
            type="button"
            onClick={() => navigate("/marketplace")}
            className="flex h-[31px] w-[31px] items-center justify-center rounded-[3px] bg-gradient-to-b from-[#D9B7B8] to-[#AB4A4F] text-white shadow-sm sm:h-[50px] sm:w-[50px] sm:rounded-[5px]"
            aria-label="Go back to marketplace"
          >
            <span className="scale-[0.62] sm:scale-100">
              <ModalLogo />
            </span>
          </button>

          {isSignedIn ? (
            <button
              type="button"
              onClick={() => navigate("/")}
              className="font-inter text-[12px] font-medium text-[#1F1F24] underline-offset-2 transition hover:underline"
            >
              Eveness
            </button>
          ) : (
            <p className="pr-8 font-inter text-[12px] font-normal text-[#636363]">
              Already have an account?{" "}
              <button
                type="button"
                onClick={openLoginModal}
                className="font-medium text-[#1F1F24] underline-offset-2 hover:underline"
              >
                Sign in
              </button>
            </p>
          )}
        </header>

        <div className="mt-[12px] flex items-center justify-center gap-x-[8px] whitespace-nowrap font-medium text-[#9F9FA9] sm:mt-[22px] sm:flex-wrap sm:gap-x-[15px] sm:gap-y-2 text-[12px]">
          <span>Unlimited try-ons</span>
          <span className="h-[2px] w-[2px] rounded-full bg-[#C8C8C8] sm:h-[3px] sm:w-[3px]" />          
          <span>Personalized matches</span>
          <span className="h-[2px] w-[2px] rounded-full bg-[#C8C8C8] sm:h-[3px] sm:w-[3px]" />
          <span>Cancel anytime</span>
        </div>

        <div className="mt-[12px] grid gap-[10px] lg:mt-[22px] lg:grid-cols-[minmax(0,810px)_minmax(330px,540px)] lg:gap-[20px]">
          <form
            onSubmit={handleSubmit}
            noValidate
            className="order-2 rounded-[5px] border border-[#DDDDDD] bg-white px-[18px] pb-[13px] pt-[20px] shadow-[0_1px_2px_-1px_rgb(0_0_0_/_0.10),0_1px_3px_0_rgb(0_0_0_/_0.10)] sm:rounded-[8px] sm:px-[32px] sm:pb-[18px] sm:pt-[34px] lg:order-1"
          >
            <h1 className="text-[24px] font-medium leading-none text-[#000000] sm:text-[27px]">
              Checkout
            </h1>

            <div className="mt-[24px] space-y-[15px] sm:mt-[34px] sm:space-y-[24px]">
              <div>
                <label
                  htmlFor="name"
                  className="mb-[7px] block font-normal uppercase tracking-[0.42em] text-[#6A7282] sm:mb-[6px] text-[10px] sm:tracking-[0.35em] font-spaceMono"
                >
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formValues.name}
                  onChange={(event) =>
                    handleFieldChange("name", event.target.value)
                  }
                  onBlur={() => handleFieldBlur("name")}
                  placeholder="Jane Doe"
                  aria-invalid={Boolean(touchedFields.name && formErrors.name)}
                  aria-describedby="name-error"
                  className={getInputClassName("name")}
                />
                {touchedFields.name && formErrors.name ? (
                  <p
                    id="name-error"
                    className="mt-[5px] font-medium text-[#DC2626] text-[12px]"
                  >
                    {formErrors.name}
                  </p>
                ) : null}
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="mb-[7px] block font-normal uppercase tracking-[0.42em] text-[#6A7282] sm:mb-[6px] text-[10px] sm:tracking-[0.35em] font-spaceMono"
                >
                  Email (We will send sign in instructions to this email)
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formValues.email}
                  onChange={(event) =>
                    handleFieldChange("email", event.target.value)
                  }
                  onBlur={() => handleFieldBlur("email")}
                  placeholder="name@example.com"
                  aria-invalid={Boolean(
                    touchedFields.email && formErrors.email,
                  )}
                  aria-describedby="email-error"
                  className={getInputClassName("email")}
                />
                {touchedFields.email && formErrors.email ? (
                  <p
                    id="email-error"
                    className="mt-[5px] font-medium text-[#DC2626] text-[12px]"
                  >
                    {formErrors.email}
                  </p>
                ) : null}
              </div>

              <div>
                <label
                  htmlFor="zipCode"
                  className="mb-[7px] block font-normal uppercase tracking-[0.42em] text-[#6A7282] sm:mb-[6px] text-[10px] sm:tracking-[0.35em] font-spaceMono"
                >
                  Zip/Post Code
                </label>
                <input
                  id="zipCode"
                  name="zipCode"
                  type="text"
                  value={formValues.zipCode}
                  onChange={(event) =>
                    handleFieldChange("zipCode", event.target.value)
                  }
                  onBlur={() => handleFieldBlur("zipCode")}
                  placeholder="12345"
                  inputMode="numeric"
                  maxLength={10}
                  aria-invalid={Boolean(
                    touchedFields.zipCode && formErrors.zipCode,
                  )}
                  aria-describedby="zip-help zip-error"
                  className={getInputClassName("zipCode")}
                />
                <p
                  id="zip-help"
                  className="mt-[5px] font-normal text-[#6A7282] text-[11px]"
                >
                  Format: 12345 or 12345-6789
                </p>
                {touchedFields.zipCode && formErrors.zipCode ? (
                  <p
                    id="zip-error"
                    className="mt-[5px] font-medium text-[#DC2626] text-[12px]"
                  >
                    {formErrors.zipCode}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="mt-[14px] grid grid-cols-3 sm:mt-[20px] gap-[10px]">
              {[
                { key: "paypal", label: "PAYPAL" },
                { key: "stripe", label: "STRIPE" },
                { key: "card", label: "CARD" },
              ].map((method) => {
                const active = paymentMethod === method.key;

                return (
                  <button
                    key={method.key}
                    type="button"
                    onClick={() => setPaymentMethod(method.key)}
                    className={[
                      "flex h-[40px] items-center gap-[5px] rounded-[3px] border bg-white px-[4px] text-left transition sm:h-[88px] sm:gap-[18px] sm:rounded-[4px] sm:px-[9px]",
                      active
                        ? "border-[#FF8B76] bg-[#FFF6F2]"
                        : "border-[#DDDDDD] hover:border-[#C8C8C8]",
                    ].join(" ")}
                  >
                    <span className="h-[18px] w-[18px] shrink-0 rounded-[2px] bg-[#D1D1D1] sm:h-[67px] sm:w-[67px] sm:rounded-[4px]" />
                    <span className="font-semibold text-[#111111] text-[12px]">
                      {method.label}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="mt-[17px] sm:mt-[38px]">
              <div className="flex items-center justify-between">
                <h2 className="font-inter font-medium text-[#0A0A0A] text-[14px]">
                  Credit card
                </h2>

                <div className="flex items-center gap-[3px] sm:gap-[5px]">
                  <span className="h-[11px] w-[10px] rounded-[1px] bg-[#FF4B18] sm:h-[20px] sm:w-[18px] sm:rounded-[2px]" />
                  <span className="h-[11px] w-[10px] rounded-[1px] bg-[#FFB000] sm:h-[20px] sm:w-[18px] sm:rounded-[2px]" />
                  <span className="h-[11px] w-[18px] rounded-[1px] bg-[#0967BD] sm:h-[20px] sm:w-[32px] sm:rounded-[2px]" />
                  <span className="h-[11px] w-[18px] rounded-[1px] bg-[#24206F] sm:h-[20px] sm:w-[32px] sm:rounded-[2px]" />
                </div>
              </div>

              <div className="mt-[12px] sm:mt-[20px]">
                <label className="mb-[6px] block uppercase tracking-[0.42em] text-[#6A7282] sm:mb-[9px] text-[10px] font-spaceMono font-normal sm:tracking-[0.36em]">
                  Card Number
                </label>
                <input
                  type="text"
                  placeholder="1111 1111 1111 1111"
                  className="h-[40px] w-full rounded-[3px] border-[1px] border-[#00000033] px-[10px] text-[#111111] outline-none transition placeholder:text-[#9B9B9B] focus:border-[#FF9A86] sm:h-[45px] sm:rounded-[4px] sm:px-[14px] text-[13px]"
                />
              </div>

              <div className="mt-[12px] grid grid-cols-3 sm:mt-[22px] gap-[14px]">
                <div>
                  <label className="mb-[6px] block uppercase tracking-[0.36em] text-[#6A7282] sm:mb-[9px] text-[10px] font-spaceMono font-normal whitespace-nowrap">
                    Expiry Month
                  </label>
                  <input
                    type="text"
                    placeholder="MM"
                    className="h-[40px] w-full rounded-[3px] border-[1px] border-[#00000033] px-[10px] outline-none transition placeholder:text-[#9B9B9B] focus:border-[#FF9A86] sm:h-[45px] sm:rounded-[4px] sm:px-[14px] text-[13px]"
                  />
                </div>

                <div>
                  <label className="mb-[6px] block uppercase tracking-[0.36em] text-[#6A7282] sm:mb-[9px] text-[10px] font-spaceMono font-normal">
                    Expiry Year
                  </label>
                  <input
                    type="text"
                    placeholder="YY"
                    className="h-[40px] w-full rounded-[3px] border-[1px] border-[#00000033] px-[10px] outline-none transition placeholder:text-[#9B9B9B] focus:border-[#FF9A86] sm:h-[45px] sm:rounded-[4px] sm:px-[14px] text-[13px]"
                  />
                </div>

                <div>
                  <label className="mb-[6px] block uppercase tracking-[0.36em] text-[#6A7282] sm:mb-[9px] text-[10px] font-spaceMono font-normal whitespace-nowrap">
                    Security Code
                  </label>
                  <input
                    type="text"
                    placeholder="11"
                    className="h-[40px] w-full rounded-[3px] border-[1px] border-[#00000033] px-[10px] outline-none transition placeholder:text-[#9B9B9B] focus:border-[#FF9A86] sm:h-[45px] sm:rounded-[4px] sm:px-[14px] text-[13px]"
                  />
                </div>
              </div>
            </div>

            <div className="mx-auto mt-[16px] max-w-[500px] text-center sm:mt-[26px]">
              <p className="font-medium text-[#0A0A0A] text-[14px] font-inter">
                By subscribing to Eveness, I confirm that:
              </p>

              <div className="mx-auto mt-1 max-w-[400px] text-left text-[#364153] sm:mt-2">
                <p className="pl-[13px] text-[14px] font-normal leading-[1.45] sm:pl-[18px] sm:leading-[1.55]">
                  I agree to the Eveness{" "}
                  <Link
                    to="/policies/terms-of-service"
                    className="underline hover:text-[#AB4A4F]"
                  >
                    terms of use
                  </Link>{" "}
                  and{" "}
                  <Link
                    to="/policies/privacy-policy"
                    className="underline hover:text-[#AB4A4F]"
                  >
                    privacy policy
                  </Link>
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="font-inter mt-[12px] h-[38px] w-full max-w-[500px] rounded-[5px] bg-[#D1D5DC] font-medium text-white transition 
                hover:bg-[#B9BEC7] disabled:cursor-not-allowed disabled:opacity-70 sm:mt-[18px] sm:h-[49px] sm:rounded-[4px] text-[14px] sm:text-[16px]"
              >
                {isSubmitting ? "Processing..." : "Complete Order"}
              </button>

              <p className="mt-[9px] flex items-center justify-center gap-[5px] font-normal text-[#8A8A92] sm:mt-[20px] sm:gap-[8px] text-[12px]">
                <span>
                  <CheckoutIcon />
                </span>
                Secure checkout
              </p>
            </div>
          </form>

          <aside className="order-1 h-fit bg-[#FFF6F1] px-[18px] py-[22px] sm:px-[22px] sm:py-[36px] lg:order-2 lg:min-h-[405px] rounded-[10px]">
            <h2 className="font-medium leading-none text-[#0A0A0A] text-[24px] font-inter">
              Order Summary
            </h2>
            <p className="mt-[10px] font-normal text-[#0A0A0A] sm:mt-[16px] text-[18px] font-inter">
              Plus Individual Plan
            </p>

            <div className="mt-[10px] sm:mt-[12px]">
              <p className="mb-[9px] font-medium text-[#0A0A0A] sm:mb-[12px] text-[14px] font-inter">
                Billing cycle
              </p>

              <label className="flex cursor-pointer items-center justify-between gap-3">
                <span className="flex items-center gap-[6px] sm:gap-[8px]">
                  <input
                    type="radio"
                    name="billingCycle"
                    checked={billingCycle === "annual"}
                    onChange={() => setBillingCycle("annual")}
                    className="accent-[#111111] h-[18px] w-[18px]"
                  />
                  <span className="text-[#0A0A0A] text-[14px] font-medium font-inter">
                    Annual{" "}
                    <span>
                      ${PRICING.annual.monthlyPrice.toFixed(2)}/month
                    </span>
                  </span>
                </span>

                <span className="font-inter rounded-[3px] bg-[#05DF72] px-[6px] py-[3px] font-medium text-black sm:rounded-[4px] sm:px-[9px] sm:py-[5px] text-[12px]">
                  {PRICING.annual.discountText}
                </span>
              </label>

              <label className="mt-[8px] flex cursor-pointer items-center gap-[6px] sm:mt-[12px] sm:gap-[8px]">
                <input
                  type="radio"
                  name="billingCycle"
                  checked={billingCycle === "monthly"}
                  onChange={() => setBillingCycle("monthly")}
                    className="accent-[#111111] h-[18px] w-[18px]"
                />
                  <span className="text-[#0A0A0A] text-[14px] font-medium font-inter">
                  Monthly{" "}
                  <span>
                    ${PRICING.monthly.monthlyPrice.toFixed(2)}/month
                  </span>
                </span>
              </label>
            </div>

            <div className="mt-[10px] border-t border-[#D6D6D6] pt-[15px] sm:mt-[17px] sm:pt-[22px]">
              <p className="font-medium text-[#0A0A0A] text-[14px] font-inter">
                {pricing.label}
              </p>

              <p className="font-inter mt-[5px] font-normal text-[#6A7282] text-[12px]">
                {pricing.priceLabel}
              </p>
              <div className="mt-[8px] space-y-[10px] text-[#111111] sm:mt-[12px] sm:space-y-[14px] text-[14px] font-inter font-normal">
                <div className="flex items-center justify-between">
                  <span>Price</span>
                  <span>{pricing.subtotalLabel}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span>Tax</span>
                  <span>{pricing.taxLabel}</span>
                </div>
              </div>
            </div>
            <div className="mt-[10px] border-t border-[#D6D6D6] pt-[15px] sm:mt-[12px] sm:pt-[22px] font-inter">
              <div className="flex items-center justify-between font-medium text-[#0A0A0A] text-[16px]">
                <span>Total</span>
                <span>{pricing.totalLabel}</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
    <Footer/>
    </>
  );
};

export default CheckoutPage;
